'use client';

import { useMemo, useState } from 'react';
import {
  DEFAULT_COIN_BORDER_STATE,
  coinBorderCss,
  coinBorderHtml,
  coinBorderSnippet,
  type CoinBorderState,
} from '@/lib/browser-coinborder-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const PREVIEW_CLASS = 'cleanor-coin-preview';

export default function BrowserCssCoinBorderGenerator() {
  const [state, setState] = useState<CoinBorderState>(DEFAULT_COIN_BORDER_STATE);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const output = useMemo(() => coinBorderSnippet(state), [state]);
  const previewCss = useMemo(() => coinBorderCss(state, PREVIEW_CLASS), [state]);
  const previewHtml = useMemo(() => coinBorderHtml(PREVIEW_CLASS), []);

  function update<K extends keyof CoinBorderState>(key: K, value: CoinBorderState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">CSS</span>
        <h2 className="tool-live-title">CSS coin border generator</h2>
        <p className="tool-live-subtitle">
          Build a decorative milled coin edge with a repeating radial dash ring. Tune the size,
          colors, and dash count, preview it live, and copy the CSS.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Face color</span>
              <input
                onChange={(e) => update('faceColor', e.target.value)}
                type="color"
                value={state.faceColor}
              />
            </label>
            <label className="tool-live-field">
              <span>Dash color</span>
              <input
                onChange={(e) => update('dashColor', e.target.value)}
                type="color"
                value={state.dashColor}
              />
            </label>
            <label className="tool-live-field">
              <span>Size: {state.size}px</span>
              <input
                max={280}
                min={80}
                onChange={(e) => update('size', Number(e.target.value))}
                type="range"
                value={state.size}
              />
            </label>
            <label className="tool-live-field">
              <span>Dash count: {state.dashCount}</span>
              <input
                max={90}
                min={8}
                onChange={(e) => update('dashCount', Number(e.target.value))}
                type="range"
                value={state.dashCount}
              />
            </label>
            <label className="tool-live-field">
              <span>Ring thickness: {state.ringThickness}px</span>
              <input
                max={30}
                min={4}
                onChange={(e) => update('ringThickness', Number(e.target.value))}
                type="range"
                value={state.ringThickness}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            className="tool-live-preview-frame"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 260,
              background: '#f8fafc',
            }}
          >
            <style>{previewCss}</style>
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>

          <label className="tool-live-field tool-live-field-full">
            <span>HTML + CSS</span>
            <textarea readOnly rows={14} value={output} />
          </label>
          <p className="tool-live-panel-note">Generated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
